import { axios } from "@/lib/axios";

export const HOME_STATIC_QUERY_STALE_TIME = 3 * 60 * 1000;
export const HOME_STATIC_QUERY_GC_TIME = 3 * 60 * 1000;

export interface HomeBannerItem {
    id: number;
    bannerName: string;
    coverImageUrl: string;
    jumpType: number;
    configUrl: string;
    configEventId: number;
    status: number;
    createTime: string;
    updateTime: string;
}

export interface HomePopup {
    id: string;
    show: boolean;
    sort: number;
    startTime?: number | null;
    endTime?: number | null;
    imageUrl: string;
    jumpType?: "h5" | "route" | "none";
    jumpUrl?: string;
}

export interface QuickEntryFrontResponse {
    id: number;
    entryName?: string;
    entryType?: number;
    eventNo?: string;
    guessNo?: string;
    eventCategory?: number;
    activityUrl?: string;
    coverImageUrl?: string;
    badgeUrl?: string;
    sort?: number;
    endTime?: string;
    eventTitle?: string;
    guessTitle?: string;
    categoryName?: string;
    parentCategoryIds?: number[];
    parentCategoryNames?: string[];
    /** Event-type only: 1 = live (used when no badgeUrl for fallback badge) */
    liveState?: number;
    /** Event-type only: 1 = hot (used when no badgeUrl for fallback badge) */
    hotFlag?: number;
}

export interface VersionInfo {
    /** 版本 */
    appVersion?: string;
    /** 下载地址 */
    downUrl?: string;
    /** 提示文案 */
    levelContent?: string;
    /** 1提示不强制,2不提示,3强更 */
    levelUp?: 1 | 2 | 3;
    /** 背景图 */
    bgUrl?: string;
    /** 图片url */
    popBack?: string;
    /** 版本号(数值) */
    versionCode?: number;
    /** 应用名 */
    appName?: string;
}

export interface VersionCheckParams {
    channelId?: string;
    versionCode?: number;
}

export interface CricketLiveEventGuessTag {
    id: number;
    tagName: string;
    sort: number;
}

export interface CricketLiveEventGuess {
    id: number;
    guessNo: string;
    title: string;
    guessCategoryId: number;
    unionType: number;
    categoryName: string;
    imageUrl: string;
    rules: string;
    status: number;
    tradeAmount: number;
    yesDesc: string;
    noDesc: string;
    yesRealtimePrice: number;
    noRealtimePrice: number;
    marketPriceUpdateTime: number;
    result: number;
    mainFlag: number;
    createTime: string;
    updateTime: string;
    endTime: string;
    userPendingOrderCount: number;
    userYesShareQuantity: number;
    userNoShareQuantity: number;
    userTraded: boolean;
    tagItems: CricketLiveEventGuessTag[];
}

export interface CricketLiveEventMatchup {
    id: number;
    title: string;
    matchupImageUrl: string;
    sort: number;
    matchRecord: string;
    abbr: string;
}

export type CricketLiveBallType = "NORMAL" | "FOUR" | "SIX" | "WIDE" | "NO_BALL" | "WICKET";
export type CricketLiveBallColor = "GREEN" | "CYAN" | "PINK" | "GRAY" | "WHITE";

export interface CricketLiveBall {
    display: string;
    legal: boolean;
    type: CricketLiveBallType;
    color: CricketLiveBallColor;
    runs: number;
}

export interface CricketLiveOver {
    displayOver: number;
    title: string;
    legalBallCount: number;
    overRuns: number;
    current: boolean;
    balls: CricketLiveBall[];
}

export interface CricketLiveTeam {
    shortName: string;
    logoUrl: string;
}

export interface CricketLiveInning {
    teamShortName: string;
    teamLogoUrl: string;
    overList: CricketLiveOver[];
}

export interface CricketLiveScore {
    display: string;
}

export interface CricketLiveOverProgress {
    display: string;
}

export interface CricketLivePanel {
    battingTeam?: CricketLiveTeam | null;
    score?: CricketLiveScore | null;
    over?: CricketLiveOverProgress | null;
    target?: number | null;
    ballsLeft?: number | null;
    crr?: number | null;
    rrr?: number | null;
    chaseBanner?: string | null;
    currentOver?: CricketLiveOver | null;
    inning1?: CricketLiveInning | null;
    inning2?: CricketLiveInning | null;
}

export interface CricketLiveEvent {
    id: number;
    eventNo: string;
    title: string;
    overview: string;
    rules: string;
    source: string;
    imageUrl: string;
    topicIds: number[];
    primaryTopicId: number;
    topicNames: string[];
    primaryTopicName: string;
    liveState: number;
    hotFlag: number;
    recommendFlag: number;
    occurTime: string;
    endTime: string;
    resultBasis: string;
    sort: number;
    tradeAmount: number;
    createTime: string;
    updateTime: string;
    eventCategory: number;
    eventLevel: string;
    guessList: CricketLiveEventGuess[];
    guessCount: number;
    matchups: CricketLiveEventMatchup[];
    hotScore: number;
    ifCricket: boolean;
    followed: boolean;
    cricketLivePanel?: CricketLivePanel | null;
}

export interface CricketLiveEventsResult {
    code: number;
    msg: string;
    total: number;
    page: number;
    data: CricketLiveEvent[];
    traceId: string;
}

export function apiGetHomeBanner() {
    return axios.post( "/api/front/banner/list/active" );
}

export async function apiGetHomeBannerList() {
    const [ isOk, res ] = await apiGetHomeBanner();
    if ( !isOk ) {
        throw res;
    }
    return Array.isArray( res?.data ) ? res.data : [];
}

export async function apiGetHomePopupList() {
    const [ isOk, res ] = await axios.post<{ data?: HomePopup[] }>( "/api/front/home-popup/list" );
    if ( !isOk ) {
        throw res;
    }

    const list = Array.isArray( res?.data ) ? res.data : [];
    return list.map( ( item ) => ( {
        id: String( item?.id ?? "" ),
        show: Boolean( item?.show ),
        sort: Number( item?.sort ?? 0 ),
        startTime: item?.startTime ?? null,
        endTime: item?.endTime ?? null,
        imageUrl: String( item?.imageUrl ?? "" ),
        jumpType: item?.jumpType,
        jumpUrl: item?.jumpUrl,
    } ) );
}

export function apiGetQuickEntryList() {
    return axios.post<{ data?: QuickEntryFrontResponse[] }>( "/api/front/quick-entry-data/list" );
}

export async function apiGetQuickEntryListData() {
    const [ isOk, res ] = await apiGetQuickEntryList();
    if ( !isOk ) {
        throw res;
    }
    return Array.isArray( res?.data ) ? res.data : [];
}

export function apiGetCricketLiveEvents() {
    return axios.get<CricketLiveEventsResult>( "/api/front/cricket/matches/live-events" );
}

export async function apiGetCricketLiveEventsList() {
    const [ isOk, res ] = await apiGetCricketLiveEvents();
    if ( !isOk ) {
        throw res;
    }
    return Array.isArray( res?.data ) ? res.data : [];
}

export function apiGetTopicEvents( params: { id: number | string; pageNum?: number; pageSize?: number } ) {
    const { id, pageNum = 1, pageSize = 20 } = params;
    return axios.get( `/api/front/topic/${id}/events`, { params: { pageNum, pageSize } } );
}

export function apiCheckVersion( params: VersionCheckParams = {} ) {
    return axios.post<{ data?: VersionInfo }>( "/api/front/version/check", params );
}
